import { useEffect, useState } from 'react'

import type { InstagramAudioSelection } from '../types'
import { useSearchInstagramAudio } from './useSearchInstagramAudio'

const UNAVAILABLE_PREVIEW_MESSAGE =
  'Meta did not return a playable preview for this track. You can still select it.'

export function isInstagramAudioPreviewUnavailable(
  message: string | null,
): boolean {
  return message === UNAVAILABLE_PREVIEW_MESSAGE
}

export function useInstagramSelectedAudioPreview(
  audio: Pick<InstagramAudioSelection, 'audio_id'> | null | undefined,
  channelId: string | null | undefined,
): {
  dataUrl: string | null
  errorMessage: string | null
  needsChannelRefresh: boolean
} {
  const { fetchPreviewDataUrl, needsChannelRefresh } = useSearchInstagramAudio()
  const [preview, setPreview] = useState<{
    audioId: string
    channelId: string
    dataUrl: string | null
    errorMessage: string | null
  } | null>(null)
  const audioId = audio?.audio_id

  useEffect(() => {
    setPreview(null)
    if (!audioId || !channelId) return

    let cancelled = false
    const loadPreview = async (): Promise<void> => {
      try {
        const url = await fetchPreviewDataUrl({ channelId, audioId })
        if (!cancelled) {
          setPreview({ audioId, channelId, dataUrl: url, errorMessage: null })
        }
      } catch (error) {
        if (!cancelled) {
          setPreview({
            audioId,
            channelId,
            dataUrl: null,
            errorMessage:
              error instanceof Error &&
              isInstagramAudioPreviewUnavailable(error.message)
                ? UNAVAILABLE_PREVIEW_MESSAGE
                : 'The music preview could not be loaded.',
          })
        }
      }
    }
    void loadPreview()

    return (): void => {
      cancelled = true
    }
  }, [audioId, channelId, fetchPreviewDataUrl])

  const currentPreview =
    preview?.audioId === audioId && preview?.channelId === channelId
      ? preview
      : null
  return {
    dataUrl: currentPreview?.dataUrl ?? null,
    errorMessage: currentPreview?.errorMessage ?? null,
    needsChannelRefresh: Boolean(
      currentPreview?.errorMessage && needsChannelRefresh,
    ),
  }
}
